import React, { useState, useEffect, useRef } from 'react';
import Button from '../common/Button';
import NumberPicker from '../common/NumberPicker';
import { scoresAPI } from '../../services/api';
import useStableford from '../../hooks/useStableford';
import { successHaptic, errorHaptic, lightHaptic } from '../../utils/haptics';

export default function GroupScoreEntry({ round, groupNumber, onScoreSubmitted }) {
  const { calculatePoints } = useStableford();

  const [currentHole, setCurrentHole] = useState(1);
  const [scores, setScores] = useState({}); // Saved scores from server
  const [pendingScores, setPendingScores] = useState({}); // Staged scores not yet saved
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [numberPickerOpen, setNumberPickerOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Prevent fetchAllScores from running during score submission
  const isSubmittingScore = useRef(false);

  // Filter players in this group
  const groupPlayers = round.players.filter(p => p.group_number === groupNumber);

  console.log('[GroupScoreEntry] Render - currentHole:', currentHole, 'scores:', scores);

  useEffect(() => {
    // Don't refetch if we're in the middle of submitting a score
    if (isSubmittingScore.current) {
      console.log('[GroupScoreEntry] Skipping fetchAllScores - score submission in progress');
      return;
    }
    console.log('[GroupScoreEntry] useEffect triggered - fetching scores');
    fetchAllScores();

    return () => {
      console.log('[GroupScoreEntry] useEffect cleanup - component unmounting or deps changed');
    };
  }, [groupNumber]); // Removed round.id - it never changes during the session

  const fetchAllScores = async (silent = false) => {
    try {
      if (!silent) setLoading(true);

      // Fetch scores for all players in the group
      const scoresData = {};
      for (const player of groupPlayers) {
        const playerScores = await scoresAPI.getByRoundPlayer(player.round_player_id);
        scoresData[player.round_player_id] = {};
        playerScores.forEach(score => {
          scoresData[player.round_player_id][score.hole_number] = score.strokes;
        });
      }

      setScores(scoresData);

      // Find first incomplete hole across all players
      for (let holeNum = 1; holeNum <= 18; holeNum++) {
        const allComplete = groupPlayers.every(player =>
          scoresData[player.round_player_id]?.[holeNum] != null
        );
        if (!allComplete) {
          setCurrentHole(holeNum);
          break;
        }
      }
    } catch (err) {
      setError('Failed to load scores: ' + err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleStrokeEdit = (player) => {
    console.log('[GroupScoreEntry] Opening picker for player:', player.player_name);
    setSelectedPlayer(player);
    setNumberPickerOpen(true);
  };

  const handleStrokeSubmit = (strokes) => {
    console.log('[GroupScoreEntry] Staging score - strokes:', strokes, 'selectedPlayer:', selectedPlayer);

    if (!selectedPlayer) {
      console.log('[GroupScoreEntry] No selected player, aborting');
      return;
    }

    // Haptic feedback for selection
    lightHaptic();

    // Stage the score (don't save yet)
    setPendingScores(prev => ({
      ...prev,
      [selectedPlayer.round_player_id]: strokes
    }));

    // Close picker
    setNumberPickerOpen(false);
    setSelectedPlayer(null);
  };

  const handleConfirmAll = async () => {
    const pendingPlayerIds = Object.keys(pendingScores);

    if (pendingPlayerIds.length === 0) {
      errorHaptic();
      setError('No scores to save');
      return;
    }

    setError('');
    setSubmitting(true);
    isSubmittingScore.current = true;

    try {
      console.log('[GroupScoreEntry] Submitting batch scores:', pendingScores);

      // Submit all pending scores
      for (const roundPlayerId of pendingPlayerIds) {
        const strokes = pendingScores[roundPlayerId];
        await scoresAPI.submit({
          roundPlayerId: parseInt(roundPlayerId),
          holeNumber: currentHole,
          strokes
        });
      }

      console.log('[GroupScoreEntry] All scores submitted successfully');

      // Success haptic
      successHaptic();

      // Clear pending scores
      setPendingScores({});

      // Refetch all scores silently — no loading spinner, so the score-entry
      // view doesn't blank/flash after every save.
      await fetchAllScores(true);

      // Auto-advance to next hole if all players have scores
      const allPlayersScored = groupPlayers.every(player =>
        scores[player.round_player_id]?.[currentHole] != null ||
        pendingScores[player.round_player_id] != null
      );

      if (allPlayersScored && currentHole < 18) {
        setCurrentHole(currentHole + 1);
      }
    } catch (err) {
      errorHaptic();
      console.error('[GroupScoreEntry] ERROR submitting batch scores:', err);
      setError('Failed to save scores: ' + err.message);
    } finally {
      setSubmitting(false);
      isSubmittingScore.current = false;
    }
  };

  const handleClearPending = () => {
    setPendingScores({});
  };

  const handlePreviousHole = () => {
    if (currentHole > 1) {
      // Clear pending scores when changing holes
      setPendingScores({});
      setCurrentHole(currentHole - 1);
    }
  };

  const handleNextHole = () => {
    if (currentHole < 18) {
      // Clear pending scores when changing holes
      setPendingScores({});
      setCurrentHole(currentHole + 1);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading scores...</div>;
  }

  const currentHoleData = round.holes.find(h => h.hole_number === currentHole);
  if (!currentHoleData) {
    return <div className="text-center py-8 text-error">Hole data not found</div>;
  }

  return (
    <div>
      {/* Group Header */}
      <div className="bg-white border-b border-gray-300 px-4 py-3 mb-4">
        <h2 className="text-lg font-semibold text-gray-900 text-center">
          GROUP #{groupNumber} - HOLE {currentHole}
        </h2>
        <div className="flex items-center justify-center gap-6 mt-2">
          <div className="text-sm text-gray-600">
            <span className="font-semibold">Par:</span> {currentHoleData.par}
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-semibold">SI:</span> {currentHoleData.stroke_index}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 text-red-800 px-4 py-3 mb-4 mx-4 border border-red-300">
          {error}
        </div>
      )}

      {/* Players Score Table */}
      <div className="bg-white border border-gray-300 mb-6 mx-4">
        {/* Column Headers */}
        <div className="bg-gray-100 px-4 py-2 flex items-center gap-3 border-b border-gray-300">
          <div className="flex-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Player</span>
          </div>
          <div className="flex-shrink-0 text-center w-20">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Shots</span>
          </div>
          <div className="flex-shrink-0 text-center w-16">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-700">Pts</span>
          </div>
        </div>

        {groupPlayers.map((player, idx) => {
          const savedStrokes = scores[player.round_player_id]?.[currentHole];
          const pendingStrokes = pendingScores[player.round_player_id];
          const displayStrokes = pendingStrokes ?? savedStrokes;
          const isPending = pendingStrokes != null;

          const points = displayStrokes === 0
            ? 0
            : displayStrokes != null
            ? calculatePoints(
                displayStrokes,
                currentHoleData.par,
                currentHoleData.stroke_index,
                player.handicap_at_time
              ).points
            : null;

          return (
            <div
              key={player.round_player_id}
              className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200 last:border-b-0"
            >
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-base">
                  {player.player_name}
                </div>
                <div className="text-xs text-gray-600">
                  HCP {player.handicap_at_time}
                </div>
              </div>

              <div className="flex-shrink-0 text-center w-20">
                <button
                  onClick={() => handleStrokeEdit(player)}
                  disabled={submitting}
                  className={`w-full border-2 rounded py-1 px-2 text-lg font-semibold transition-all disabled:opacity-50 min-h-[44px] ${
                    isPending
                      ? 'bg-yellow-100 border-yellow-500 text-yellow-900 hover:bg-yellow-200'
                      : 'border-primary text-primary hover:bg-primary hover:text-white'
                  }`}
                >
                  {displayStrokes === 0 ? 'NR' : displayStrokes ?? '–'}
                </button>
              </div>

              <div className="flex-shrink-0 text-center w-16">
                <div className={`text-lg font-semibold min-h-[44px] flex items-center justify-center ${
                  isPending ? 'text-yellow-700' : 'text-red-600'
                }`}>
                  {points ?? '–'}
                </div>
              </div>
            </div>
          );
        })}
      </div>


      {/* Confirm All Scores Button */}
      {Object.keys(pendingScores).length > 0 && (
        <div className="px-4 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleClearPending}
              disabled={submitting}
              className="py-4 bg-gray-200 text-gray-700 font-bold text-lg rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 min-h-touch-lg"
            >
              Clear All
            </button>
            <button
              onClick={handleConfirmAll}
              disabled={submitting}
              className="py-4 bg-green-600 text-white font-bold text-lg rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 min-h-touch-lg"
            >
              {submitting ? 'Saving...' : 'Confirm All'}
            </button>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="grid grid-cols-2 gap-3 px-4">
        <Button
          variant="secondary"
          onClick={handlePreviousHole}
          disabled={currentHole === 1}
          size="lg"
          className="w-full bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50 disabled:opacity-40"
        >
          ← Previous
        </Button>
        <Button
          variant="secondary"
          onClick={handleNextHole}
          disabled={currentHole === 18}
          size="lg"
          className="w-full bg-white border-2 border-gray-300 text-gray-900 hover:bg-gray-50 disabled:opacity-40"
        >
          Next Hole →
        </Button>
      </div>

      {/* Number Picker Modal */}
      <NumberPicker
        isOpen={numberPickerOpen}
        onClose={() => {
          setNumberPickerOpen(false);
          setSelectedPlayer(null);
        }}
        onSelect={handleStrokeSubmit}
        title={selectedPlayer ? `${selectedPlayer.player_name} - Hole ${currentHole}` : 'Select Strokes'}
        min={1}
        max={8}
      />
    </div>
  );
}
